import React, { useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { ref, remove, set } from "firebase/database";
import { deleteToken, getToken, isSupported } from "firebase/messaging";

import { database, messaging } from "../../../firebase/firebase";
import { useAuth } from "../../../contexts/AuthContext";
import { useNotification } from "../../../contexts/NotificationContext";
import { Button } from "../../../components/ui/button";
import { Item } from "./Item";
import { Title } from "./Title";

const VAPID_KEY =
  "BDH5wygb9eIU9-z35Gb3FB5pLzcgFM92trroPNtDf5TuokVt0neODRj0i2nT601R0GW3WVLSvn2byLyWqGKjeks";

export default function Notifications() {
  const [permission, setPermission] = useState<NotificationPermission | null>(
    null
  );
  const [isTokenRevoked, setIsTokenRevoked] = useState(false);
  const [isSupportedBrowser, setIsSupportedBrowser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { user } = useAuth();
  const { showNotification } = useNotification();

  useEffect(() => {
    const checkSupport = async () => {
      if (typeof window !== "undefined" && "Notification" in window) {
        setPermission(Notification.permission);
        setIsSupportedBrowser(true);

        // Verificar se o messaging é suportado
        const supported = await isSupported();
        if (!supported) {
          setIsSupportedBrowser(false);
        }
      } else {
        setIsSupportedBrowser(false);
      }
      setIsLoading(false);
    };

    checkSupport();
  }, []);

  useEffect(() => {
    const checkToken = async () => {
      if (
        permission === "granted" &&
        !isTokenRevoked &&
        isSupportedBrowser &&
        user
      ) {
        try {
          const token = await getToken(messaging, { vapidKey: VAPID_KEY });
          if (!token) {
            setIsTokenRevoked(true);
          }
        } catch (error: any) {
          console.error("Error checking token:", error);
          // Se o erro for de permissão negada ou token inválido, marcar como revogado
          if (
            error.code === "messaging/permission-blocked" ||
            error.code === "messaging/token-unsubscribe-failed"
          ) {
            setIsTokenRevoked(true);
          }
        }
      }
    };

    checkToken();
  }, [permission, isTokenRevoked, isSupportedBrowser, user]);

  const getDeviceType = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/mobile/i.test(userAgent)) return "mobile";
    if (/tablet/i.test(userAgent)) return "tablet";
    return "desktop";
  };

  const storeToken = async (token: string) => {
    const deviceType = getDeviceType();
    try {
      await set(
        ref(database, `subscriptions/${user.uid}/${deviceType}`),
        token
      );
    } catch (err) {
      showNotification("error", "Erro ao salvar o token no banco de dados.");
    }
  };

  const waitForServiceWorker =
    async (): Promise<ServiceWorkerRegistration | null> => {
      if (!("serviceWorker" in navigator)) {
        return null;
      }

      try {
        // Aguardar o service worker estar pronto
        const registration = await navigator.serviceWorker.ready;
        return registration;
      } catch (error) {
        console.error("Service Worker not ready:", error);
        // Tentar registrar se não estiver registrado
        try {
          const registration = await navigator.serviceWorker.register(
            "/firebase-messaging-sw.js"
          );
          await navigator.serviceWorker.ready;
          return registration;
        } catch (regError) {
          console.error("Failed to register service worker:", regError);
          return null;
        }
      }
    };

  const handleRequestNotificationPermission = async () => {
    try {
      if (!isSupportedBrowser) {
        showNotification(
          "error",
          "Seu navegador não suporta notificações push."
        );
        return;
      }

      if (permission === "default" || permission === null) {
        // Aguardar service worker estar pronto
        const registration = await waitForServiceWorker();
        if (!registration) {
          showNotification(
            "error",
            "Service Worker não está disponível. Recarregue a página e tente novamente."
          );
          return;
        }

        const result = await Notification.requestPermission();
        setPermission(result);

        if (result === "granted") {
          try {
            // Aguardar um pouco para garantir que o service worker está totalmente ativo
            await new Promise((resolve) => setTimeout(resolve, 500));

            const token = await getToken(messaging, { vapidKey: VAPID_KEY });
            if (token) {
              await storeToken(token);
              setIsTokenRevoked(false);
              showNotification("success", "Notificações ativadas com sucesso!");
            } else {
              showNotification(
                "error",
                "Não foi possível obter o token de notificação. Tente novamente."
              );
            }
          } catch (tokenError: any) {
            console.error("Error getting token:", tokenError);
            if (tokenError.code === "messaging/permission-blocked") {
              showNotification(
                "error",
                "Permissão de notificação foi bloqueada. Acesse as configurações do navegador para permitir."
              );
            } else if (tokenError.code === "messaging/token-subscribe-failed") {
              showNotification(
                "error",
                "Erro ao registrar o token. Verifique se o Service Worker está ativo."
              );
            } else if (
              tokenError.code === "messaging/failed-service-worker-registration"
            ) {
              showNotification(
                "error",
                "Service Worker não está registrado. Recarregue a página e tente novamente."
              );
            } else {
              showNotification(
                "error",
                `Erro ao obter token: ${
                  tokenError.message || "Erro desconhecido"
                }`
              );
            }
          }
        } else if (result === "denied") {
          showNotification(
            "warning",
            "Notificações foram negadas. Acesse as configurações do navegador para permitir."
          );
        }
      } else if (permission === "denied") {
        showNotification(
          "warning",
          "Notificações foram bloqueadas. Acesse as configurações do navegador para permitir."
        );
      }
    } catch (err: any) {
      console.error("Error requesting permission:", err);
      if (err.name === "TypeError" || err.message?.includes("not supported")) {
        showNotification(
          "error",
          "Seu navegador não suporta notificações push."
        );
      } else {
        showNotification(
          "error",
          `Erro ao solicitar permissão: ${err.message || "Erro desconhecido"}`
        );
      }
    }
  };

  const handleRevokeToken = async () => {
    try {
      await deleteToken(messaging);
      const device = getDeviceType();
      await remove(ref(database, `subscriptions/${user.uid}/${device}`));
      setIsTokenRevoked(true);
      showNotification("info", "Token revogado e removido da base.");
    } catch (err) {
      showNotification("error", "Erro ao revogar o token.");
    }
  };

  const handleReenableNotifications = async () => {
    try {
      if (permission !== "granted") {
        showNotification(
          "warning",
          "Permissão de notificação não foi concedida. Solicite permissão primeiro."
        );
        return;
      }

      // Aguardar service worker estar pronto
      const registration = await waitForServiceWorker();
      if (!registration) {
        showNotification(
          "error",
          "Service Worker não está disponível. Recarregue a página e tente novamente."
        );
        return;
      }

      // Tentar deletar token antigo (pode não existir)
      try {
        await deleteToken(messaging);
      } catch (deleteError) {
        // Ignorar erro se o token não existir
        console.log("Token não existe ou já foi deletado");
      }

      // Aguardar um pouco para garantir que o service worker está totalmente ativo
      await new Promise((resolve) => setTimeout(resolve, 500));

      const token = await getToken(messaging, { vapidKey: VAPID_KEY });
      if (token) {
        await storeToken(token);
        setIsTokenRevoked(false);
        showNotification("success", "Notificações habilitadas novamente!");
      } else {
        showNotification(
          "error",
          "Não foi possível obter o token. Verifique as permissões do navegador."
        );
      }
    } catch (err: any) {
      console.error("Error re-enabling notifications:", err);
      if (err.code === "messaging/permission-blocked") {
        showNotification(
          "error",
          "Permissão de notificação foi bloqueada. Acesse as configurações do navegador."
        );
      } else if (err.code === "messaging/failed-service-worker-registration") {
        showNotification(
          "error",
          "Service Worker não está registrado. Recarregue a página e tente novamente."
        );
      } else {
        showNotification(
          "error",
          `Erro ao habilitar notificações: ${
            err.message || "Erro desconhecido"
          }`
        );
      }
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <p className="text-sm text-muted-foreground">
          Verificando suporte a notificações...
        </p>
      );
    }

    if (!isSupportedBrowser) {
      return (
        <p className="text-sm text-muted-foreground">
          Seu navegador não suporta notificações push. Use um navegador moderno
          como Chrome, Firefox ou Edge.
        </p>
      );
    }

    if (permission === "granted" && !isTokenRevoked) {
      return (
        <>
          <p className="text-sm text-muted-foreground">
            Você receberá alertas sobre as viagens. Pode desativar quando
            quiser; é possível reativar mais tarde.
          </p>
          <Button
            variant="destructive"
            className="w-full"
            onClick={handleRevokeToken}
          >
            <BellOff className="mr-2 h-4 w-4" /> Desativar notificações
          </Button>
        </>
      );
    }
    if (permission === "granted" && isTokenRevoked) {
      return (
        <>
          <p className="text-sm text-muted-foreground">
            As notificações estão desativadas para este dispositivo. Clique no
            botão abaixo para reativar.
          </p>
          <Button className="w-full" onClick={handleReenableNotifications}>
            <BellRing className="mr-2 h-4 w-4" /> Reativar notificações
          </Button>
        </>
      );
    }
    if (permission === "default" || permission === null) {
      return (
        <>
          <p className="text-sm text-muted-foreground">
            Ative as notificações para receber alertas sobre novas viagens e
            lembretes importantes.
          </p>
          <Button
            className="w-full"
            onClick={handleRequestNotificationPermission}
          >
            <Bell className="mr-2 h-4 w-4" /> Ativar notificações
          </Button>
        </>
      );
    }
    return (
      <>
        <p className="text-sm text-muted-foreground">
          As notificações foram bloqueadas. Acesse as configurações do navegador
          para permitir notificações deste site.
        </p>
        <p className="text-xs text-muted-foreground/70 mt-2">
          No Chrome/Edge: Configurações → Privacidade e segurança →
          Configurações do site → Notificações
        </p>
      </>
    );
  };

  return (
    <section className="space-y-3">
      <Title>Notificações</Title>
      <Item className="space-y-3">{renderContent()}</Item>
    </section>
  );
}
